/**
 * Sentry event-filter predicates — shared across client, server, and edge runtimes.
 *
 * ErrorEvent predicates:
 *   Each inspects a Sentry `ErrorEvent` and returns `true` when the event is
 *   recognised SDK-self-noise to be dropped from `beforeSend`. Keep predicates
 *   narrow — pattern + stack-frame anchor — so application bugs with overlapping
 *   messages still surface.
 *
 * TransactionEvent predicates:
 *   Each inspects a `TransactionEvent` (performance trace) and returns `true`
 *   when the event is recognised perf-noise to be dropped from
 *   `beforeSendTransaction`. Narrow by route + span-shape so real perf regressions
 *   on the same route still surface.
 *
 * Companion test: tests/sentry/event-filters.test.ts
 *
 * Related: [[knowledge/bug-patterns]] [[rules/learned-patterns]]
 */

import type { ErrorEvent, TransactionEvent } from '@sentry/core';

/** Span/trace status strings the SDK maps 401/403 responses onto. */
const EXPECTED_4XX_SPAN_STATUSES = new Set(['unauthenticated', 'permission_denied']);
/** Expected authorization HTTP codes from createReadHandler (401 JWT-refresh window, 403 scope denial). */
const EXPECTED_4XX_HTTP_CODES = new Set([401, 403]);

function extractHttpStatusCode(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const code = (data as Record<string, unknown>)['http.response.status_code'];
  return typeof code === 'number' ? code : null;
}

/**
 * Node internal webstreams `TransformStream` controller noise emitted by Sentry's
 * own `nativeNodeFetchIntegration` while patching the stream during RSC payload
 * streaming. Stack is 100% Node internals; the underlying fetch already completed.
 *
 * Sentry issue 112518326 — `TypeError: controller[kState].transformAlgorithm is
 * not a function` at `node:internal/webstreams/transformstream:527,568`.
 *
 * Filter is intentionally narrow: requires the literal message AND at least one
 * stack frame inside `node:internal/webstreams`. App code that throws
 * `transformAlgorithm is not a function` from its own TransformStream usage
 * would surface a frame in `src/*` and pass through.
 */
export function isNodeWebstreamsSdkNoise(event: ErrorEvent): boolean {
  const exceptionValue = event.exception?.values?.[0];
  if (!exceptionValue) return false;

  const message = exceptionValue.value || '';
  if (!/transformAlgorithm is not a function/.test(message)) return false;

  const frames = exceptionValue.stacktrace?.frames || [];
  return frames.some((f) => {
    // ErrorEvent stack frames have `filename`; older SDK paths use `abs_path`.
    // Cast covers both shapes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path = (f.filename || (f as any).abs_path || '') as string;
    return /node:internal\/webstreams/.test(path);
  });
}

/**
 * True when the Sentry event targets the error-reporting endpoints themselves —
 * meaning the outbound delivery of a prior error report failed at the transport
 * layer. Capturing this as a Sentry event creates an infinite-loop noise class.
 *
 * Covers two shapes that appear in different SDK / runtime paths:
 *   (a) `event.request.url` contains `/api/error-reports` or `/api/system/incidents`
 *       (SDK sets this from `nativeNodeFetchIntegration` or `winterCGFetchIntegration`
 *        span context when available).
 *   (b) `event.transaction` contains the same paths (server-runtime route name, e.g.
 *       "POST /api/error-reports" recorded by the Next.js server instrumentation).
 *
 * Sentry issues: 110890554 (client self-loop, 31 events Apr–May 2026),
 *                128361975 (server-runtime transient fetch, node tags runtime.name=node).
 *
 * Shared across client, server, and edge runtimes so the guard is applied uniformly
 * wherever the Sentry SDK captures errors. Previously existed only in
 * `instrumentation-client.ts` beforeSend — server/edge were unguarded.
 *
 * The lookaheads `(?=[\/\s?#]|$)` ensure that `/api/error-reports-archive`
 * does NOT match — only exact segment endings (slash, query, hash, or string end)
 * are accepted. A bare `\b` would fire on the `-` that follows `reports` because
 * `-` is not a word character, producing false positives on hyphenated suffixes.
 */
const ERROR_REPORTER_LOOP_RE =
  /\/api\/error-reports(?=[\/\s?#]|$)|\/api\/system\/incidents(?=[\/\s?#]|$)/;

export function isErrorReporterLoopEvent(event: ErrorEvent): boolean {
  const requestUrl = event.request?.url ?? '';
  const transaction = event.transaction ?? '';
  return ERROR_REPORTER_LOOP_RE.test(requestUrl) || ERROR_REPORTER_LOOP_RE.test(transaction);
}

/**
 * Canonical regex for transient browser-level fetch-transport failures.
 *
 * `fetch()` throws these when the HTTP request cannot complete at all — DNS
 * failure, offline, tab throttled, Server Action POST dropped mid-flight.
 * Covers every browser's phrasing:
 *   - Chrome:  `Failed to fetch`  /  `network error` (lowercase, with a space)
 *   - Safari:  `Load failed`
 *   - Firefox: `NetworkError when attempting to fetch resource` (no space)
 *
 * The `\b` anchors on `network error` keep it from swallowing genuine
 * application errors that merely contain the phrase inside a longer sentence.
 *
 * Used by `beforeSend` in `instrumentation-client.ts`, `sentry.server.config.ts`,
 * and `sentry.edge.config.ts` — shared across all runtimes.
 */
const TRANSIENT_NETWORK_ERROR_RE = /Failed to fetch|Load failed|NetworkError|\bnetwork error\b/i;

/**
 * True when the error message+type represent a transient fetch-transport
 * failure that is client-side noise (offline / DNS / throttled tab), not an
 * application bug. Type must be `TypeError` (raw fetch reject) or `Error`
 * (rewrapped by `@supabase/sentry-js-integration`).
 *
 * Sentry issue 93061848 — `TypeError: network error` from
 * `[Client] AppLayout.fetchModuleAccess`, 22 events / 7 users. The failure is
 * already caught locally (try/catch sets `hasAIAccess=false`); reporting it to
 * Sentry adds no actionable signal.
 */
export function isTransientNetworkError(type: string, value: string): boolean {
  if (!value) return false;
  if (type !== 'TypeError' && type !== 'Error') return false;
  return TRANSIENT_NETWORK_ERROR_RE.test(value);
}

/**
 * Expected-authorization-decision noise auto-captured by
 * `@supabase/sentry-js-integration` (errors:true) at the SDK layer.
 *
 * ROOT-CAUSE (Sentry issue 127427425): The Supabase Sentry integration is wired
 * with `errors: true` (safe-supabase-integration.ts + each Sentry.init). It hooks
 * the Supabase client itself, so EVERY failed `supabase.rpc()` / query is captured
 * as a Sentry exception (`handled: yes`, stack frame inside
 * `@supabase/sentry-js-integration`) BEFORE any application try/catch runs. The
 * app-level guard `isExpectedNotificationDenial` only gates the MANUAL
 * `captureClientError` path — it is structurally incapable of reaching this
 * SDK auto-capture. So a permission-denied (Postgres 42501) or JWT-expiry
 * (PostgREST PGRST301 / HTTP 401) denial — both EXPECTED authorization outcomes for
 * anon / expired-session pollers, or the sub-second PostgREST schema-reload window —
 * is double-reported, unfiltered, on every poll. The grant migration shipped for
 * the same issue (commit 624844107) could not close it because the noise originates
 * in the SDK capture, not in app code or a missing grant.
 *
 * This is the ONLY layer (beforeSend) that can intercept the SDK auto-capture.
 *
 * NARROW GUARDS — pattern AND a supabase-integration anchor:
 *   1. The message must read as an expected authorization denial:
 *        - `permission denied for function` / `permission denied` (Postgres 42501)
 *        - `PGRST301` marker (PostgREST JWT-expiry)
 *        - JWT-expiry phrasing: `JWT expired` / `JWSError` / `401`
 *   2. The event must carry a signal proving this is the supabase SDK auto-capture,
 *      NOT a genuine application bug whose own `src/*` frame says "permission denied".
 *      EITHER of two anchors satisfies this (see below).
 *
 * REGRESSION ROOT-CAUSE (Sentry 127427425, why prior fix commit 4b112d68 did not hold):
 * The original anchor required a stack frame whose path contains
 * `@supabase/sentry-js-integration`. But `beforeSend` runs CLIENT-side, in the browser,
 * BEFORE Sentry's server-side sourcemap symbolication. At that moment every client stack
 * frame is a MINIFIED Turbopack chunk URL (e.g. `/_next/static/chunks/<hash>.js`) — the
 * `@supabase/sentry-js-integration` substring only materialises AFTER server-side
 * symbolication at Sentry ingest, far too late to influence `beforeSend`. So the frame
 * anchor matched on the SERVER but NEVER on the client (this issue is browser/Turbopack),
 * letting every client poll-denial flood Sentry. The companion regression test passed
 * only because it fed the post-symbolication path — it never reproduced the client shape.
 *
 * MINIFICATION-SAFE ANCHOR: the @supabase/sentry-js-integration (breadcrumbs:true) also
 * records every DB op as a breadcrumb with `type: 'supabase'`. Breadcrumbs are plain event
 * data — NOT subject to source minification — so they survive verbatim to client-side
 * `beforeSend`. A `type: 'supabase'` breadcrumb is emitted by nothing else, so combined
 * with the denial message it stays narrow: a genuine app permission bug NOT routed through
 * the instrumented supabase client carries no such breadcrumb and PASSES THROUGH.
 *
 * Used by `beforeSend` in `instrumentation-client.ts`, `sentry.server.config.ts`,
 * and `sentry.edge.config.ts` — shared across all runtimes.
 */
const EXPECTED_SUPABASE_DENIAL_MESSAGE_RE =
  /permission denied(?: for [\w]+)?|\b42501\b|\bPGRST301\b|JWT expired|JWSError|\b401\b/i;

const SUPABASE_SENTRY_INTEGRATION_FRAME_RE = /@supabase\/sentry-js-integration/;

/**
 * Anchor A — a stack frame whose path contains `@supabase/sentry-js-integration`.
 * Present on server/edge runtimes and on client events AFTER server-side symbolication.
 * On the client at beforeSend time this is absent (frames are still minified).
 */
function hasSupabaseIntegrationFrame(
  exceptionValue: NonNullable<NonNullable<ErrorEvent['exception']>['values']>[number]
): boolean {
  const frames = exceptionValue.stacktrace?.frames || [];
  return frames.some((f) => {
    // ErrorEvent stack frames have `filename`; older SDK paths use `abs_path`.
    // Both module ('@supabase/...') and bundled-path ('node_modules/@supabase/...')
    // shapes are covered by the substring anchor.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path = (f.filename || (f as any).abs_path || (f as any).module || '') as string;
    return SUPABASE_SENTRY_INTEGRATION_FRAME_RE.test(path);
  });
}

/**
 * Anchor B — a `type: 'supabase'` breadcrumb emitted by the integration's breadcrumbs
 * feature. Breadcrumbs are not minified, so this survives to client-side beforeSend and
 * closes the client hole that Anchor A structurally cannot reach.
 */
function hasSupabaseIntegrationBreadcrumb(event: ErrorEvent): boolean {
  const crumbs = event.breadcrumbs || [];
  return crumbs.some((b) => b?.type === 'supabase');
}

export function isExpectedSupabaseDenial(event: ErrorEvent): boolean {
  const exceptionValue = event.exception?.values?.[0];
  if (!exceptionValue) return false;

  const message = exceptionValue.value || '';
  if (!EXPECTED_SUPABASE_DENIAL_MESSAGE_RE.test(message)) return false;

  // EITHER anchor proves this is the supabase SDK auto-capture (not an app bug with an
  // overlapping message). Anchor A covers server/edge + post-symbolication; Anchor B
  // (breadcrumb) is the minification-safe path that fixes the client regression.
  return hasSupabaseIntegrationFrame(exceptionValue) || hasSupabaseIntegrationBreadcrumb(event);
}

// ─── Performance-event predicates ────────────────────────────────────────────

/**
 * True when a Sentry `TransactionEvent` (performance trace) targets the
 * error-reporting endpoints themselves — meaning the outbound delivery of a
 * prior error/incident report generated a Sentry *performance* envelope rather
 * than an exception envelope.
 *
 * This is the TransactionEvent sibling of `isErrorReporterLoopEvent` (which
 * only guards the ErrorEvent / `beforeSend` path). Sentry issue 128460538 is
 * the specific manifestation: a "Failed HTTP Operation" TransactionEvent with
 * level:warning, `event.transaction = "POST /api/system/incidents"`, an
 * `http.client` span whose description matches the same route, and NO
 * exception block. Because `beforeSendTransaction` previously only called
 * `isKanbanColumnN1PerfNoise` (which checks the /leads Kanban shape), the
 * self-loop leaked as a performance event — ErrorEvent predicates are never
 * reached inside `beforeSendTransaction`.
 *
 * NARROW GUARDS (prevents over-broad suppression):
 *   1. `event.transaction` must match `/api/error-reports` or
 *      `/api/system/incidents` via the shared `ERROR_REPORTER_LOOP_RE`.
 *      OR at least one span `description` matches the same pattern — covering
 *      the shape where the transaction name is the parent route but the span
 *      description carries the outbound POST.
 *   2. The event must have NO `exception.values` — if an exception is present
 *      this is a real error captured under a transaction envelope and must
 *      surface (same safety valve as `isKanbanColumnN1PerfNoise`).
 *
 * SAFETY: A genuine perf regression or error on `/api/system/incidents` that
 * carries an exception body will NOT be suppressed (guard 2 passes it through).
 *
 * Sentry issue 128460538 — TransactionEvent level:warning, no exception,
 * transaction="POST /api/system/incidents".
 *
 * @see isErrorReporterLoopEvent — ErrorEvent / beforeSend sibling
 * @see instrumentation-client.ts `beforeSendTransaction`
 * @see sentry.server.config.ts `beforeSendTransaction`
 * @see sentry.edge.config.ts `beforeSendTransaction`
 */
export function isErrorReporterLoopTransaction(event: TransactionEvent): boolean {
  // Guard 2: if exception is present, this is a real error — do NOT suppress.
  if (event.exception?.values?.length) return false;

  // Guard 1a: transaction name matches the reporter loop routes.
  const transaction = event.transaction ?? '';
  if (ERROR_REPORTER_LOOP_RE.test(transaction)) return true;

  // Guard 1b: any span description matches — covers the shape where the parent
  // transaction is a wrapping route but the outbound http.client span carries
  // the actual POST /api/system/incidents or /api/error-reports URL.
  const spans = event.spans ?? [];
  for (const span of spans) {
    const desc = span.description ?? '';
    if (ERROR_REPORTER_LOOP_RE.test(desc)) return true;
  }

  return false;
}

/**
 * True when a Sentry `TransactionEvent` (performance trace) represents the
 * N+1 column fetch pattern on the /leads Kanban board and should be dropped
 * from `beforeSendTransaction`.
 *
 * ROOT-CAUSE (Sentry issue 127381445 — level:info, no exception):
 * The /leads Kanban fired one `GET /api/reads/leads/column` per pipeline stage
 * via `useColumnLeads` / `getColumnLeads` on initial mount because
 * `LeadsPageContent.tsx` did not server-prefetch the per-column InfiniteData.
 * Sentry's N+1 detector saw N repeated http.client spans for the same URL
 * prefix and emitted a level:info TransactionEvent.
 *
 * THE REAL FIX (shipped 2026-06-30):
 * `LeadsPageContent.tsx` now calls `queryClient.prefetchInfiniteQuery` for each
 * visible stage, keyed as `queryKeys.leads.columnList(orgId, slug, undefined)`.
 * `CacheProfiles.ENTITY` staleTime=5 min → the hydrated data is fresh on mount →
 * `refetchOnMount: true` does NOT fire → initial board load goes from 9 client
 * column requests to 0.
 *
 * WHY THE FILTER REMAINS (transitional shield):
 *   (a) Stale client bundles during Vercel rollout still run the old code path and
 *       will fire the N+1 — the filter prevents Sentry quota burn during deploy.
 *   (b) Intentional per-column fetches on FILTERED or SEARCHED loads (user applies
 *       a filter: assigned_to, tag, date range, etc.) remain client-side because
 *       the server prefetch only covers the unfiltered initial load. These are
 *       genuinely out-of-scope per the approved design decision and should not fill
 *       the Sentry N+1 queue.
 *   (c) The "Load more" fetch within a single column (cursor pagination) also hits
 *       the same URL — filtering by ≥3 simultaneous spans rules these out (a single
 *       "Load more" is 1 span, not 3).
 *
 * NARROW GUARDS (prevents over-broad suppression):
 *   1. `event.transaction` must match `/leads` route (NOT /clients, /tasks, etc.).
 *   2. The event must contain ≥3 spans whose `description` matches the
 *      `/api/reads/leads/column` URL prefix — the N+1 fingerprint. A single or
 *      two such spans could be legitimate; ≥3 mirrors the Sentry N+1 threshold.
 *   3. `event.level` must be absent (raw SDK envelope shape — Relay stamps
 *      `level:info` server-side AFTER this hook, so a raw TransactionEvent
 *      never carries it) OR `"info"` (stored Sentry-UI shape). An explicit
 *      `"error"`/`"warning"` level passes the event through untouched.
 *
 * SAFETY: A genuine bug that causes a server error on /leads with many concurrent
 * column fetches will NOT be dropped — `level` would be "error" and the
 * `exception` block would be present.
 *
 * Sentry issue 127381445 — N+1 API Call (spans, level info, no exception).
 *
 * @see LeadsPageContent.tsx — `prefetchInfiniteQuery` per visible stage (real fix)
 * @see instrumentation-client.ts `beforeSendTransaction`
 * @see sentry.server.config.ts `beforeSendTransaction`
 * @see sentry.edge.config.ts `beforeSendTransaction`
 */

/**
 * True when a Sentry `TransactionEvent` is Sentry's perf-detector noise for an
 * expected 4xx response on an `/api/reads/*` route — and should be dropped from
 * `beforeSendTransaction`.
 *
 * ROOT-CAUSE (Sentry issue 128460537 — level:warning stored shape, no exception;
 * raw envelope carries no `level` at all — see REGRESSION note below):
 * `/api/reads/clients/settings` (and sibling `/api/reads/*` routes built with
 * `createReadHandler`) intentionally return 4xx HTTP status codes for expected
 * authorization outcomes:
 *   - 401 — auth headers absent (JWT-refresh window, sub-second transient)
 *   - 403 — permission denied / data-scope none (terminal, non-reportable)
 * These are correct behaviour from `classifyFailureStatus()` in
 * `create-read-handler.ts`. Sentry's "Failed HTTP Operation" perf-detector sees
 * the 4xx response code on an `http.client` span and emits a `TransactionEvent`
 * with `level:warning` and no exception body. There is nothing to fix — these
 * are expected authorization decisions, not bugs.
 *
 * REGRESSION ROOT-CAUSE (why the original `level === 'warning'` guard was dead
 * code, same CLASS as Sentry 127381445): raw SDK TransactionEvents carry NO
 * `level` — @sentry/core builds the transaction payload without one; Sentry's
 * Relay stamps `level:warning` server-side AFTER `beforeSendTransaction` runs.
 * The strict literal guard was written against the stored Sentry-UI issue
 * shape and rejected every real envelope. Removing the level guard outright
 * would make this predicate over-broad — nearly every `/api/reads/*` page
 * touches this route, so it now requires EXPLICIT 4xx evidence instead.
 *
 * NARROW GUARDS (prevents over-broad suppression):
 *   1. The event must have NO `exception.values` — if an exception is present
 *      this is a real error captured under a transaction envelope and must
 *      surface (same safety valve as `isKanbanColumnN1PerfNoise`).
 *   2. Explicit 401/403 evidence must exist on the reads surface itself:
 *      - Server shape (the transaction IS the reads route): `contexts.trace`
 *        span status (`unauthenticated`/`permission_denied`), its
 *        `http.response.status_code` data attribute, or `contexts.response.status_code`.
 *      - Client shape (a reads `http.client` span inside a page transaction):
 *        the matching span's own `status` or `http.response.status_code` data.
 *   3. FAIL-OPEN: when the transaction/span matches `/api/reads/` but carries
 *      NO 4xx evidence, the event is NOT dropped — worst case is one Sentry
 *      noise event we tune from a real sample, never silently-dropped real
 *      perf data.
 *
 * SAFETY: A genuine server fault on an `/api/reads/*` route (DB error,
 * unexpected exception) would return HTTP 500 and produce an `exception.values`
 * block (guard 1) — Sentry's perf detector also does not classify 500s as
 * "Failed HTTP Operation" noise (it only fires on 4xx).
 *
 * Sentry issue 128460537 — "Failed HTTP Operation" on /api/reads/clients/settings
 * (raw envelope carries no `level`; Relay stamps `level:warning` post-ingestion).
 *
 * @see createReadHandler (src/app/api/reads/_lib/create-read-handler.ts) — classifyFailureStatus
 * @see instrumentation-client.ts `beforeSendTransaction`
 * @see sentry.server.config.ts `beforeSendTransaction`
 * @see sentry.edge.config.ts `beforeSendTransaction`
 */

const READS_ROUTE_SPAN_RE = /\/api\/reads\//;

export function isReadsExpected4xxPerfNoise(event: TransactionEvent): boolean {
  // Guard 1: if exception is present, this is a real error — do NOT suppress.
  if (event.exception?.values?.length) return false;

  // Guard 2 (replaces the dead `level === 'warning'` guard — raw SDK
  // TransactionEvents never carry `level`; Relay stamps it server-side AFTER
  // this hook, so the old guard rejected every real envelope. CLASS: predicate
  // written against the stored Sentry-UI issue shape instead of the raw
  // envelope shape): explicit 401/403 evidence must exist on the reads
  // surface itself. FAIL-OPEN: no status evidence → pass the event through —
  // worst case is one Sentry noise event we tune with a real sample, never
  // silently-dropped real perf data.

  // Server shape: the transaction IS the reads route (e.g. "GET
  // /api/reads/clients/settings"); 4xx evidence lives on the event-level
  // trace/response contexts.
  const transaction = event.transaction ?? '';
  if (READS_ROUTE_SPAN_RE.test(transaction)) {
    const trace = event.contexts?.trace as
      | { status?: string; data?: Record<string, unknown> }
      | undefined;
    if (trace?.status && EXPECTED_4XX_SPAN_STATUSES.has(trace.status)) return true;
    const traceCode = extractHttpStatusCode(trace?.data);
    if (traceCode !== null && EXPECTED_4XX_HTTP_CODES.has(traceCode)) return true;
    const response = event.contexts?.response as { status_code?: number } | undefined;
    if (
      typeof response?.status_code === 'number' &&
      EXPECTED_4XX_HTTP_CODES.has(response.status_code)
    ) {
      return true;
    }
    return false;
  }

  // Client shape: a reads http.client span inside a page transaction must
  // itself carry the 401/403 (span status or span data status code).
  const spans = event.spans ?? [];
  for (const span of spans) {
    const desc = span.description ?? '';
    if (!READS_ROUTE_SPAN_RE.test(desc)) continue;
    const spanStatus = (span as { status?: string }).status;
    if (spanStatus && EXPECTED_4XX_SPAN_STATUSES.has(spanStatus)) return true;
    const spanCode = extractHttpStatusCode((span as { data?: unknown }).data);
    if (spanCode !== null && EXPECTED_4XX_HTTP_CODES.has(spanCode)) return true;
  }

  return false;
}

/** Minimum repeated /api/reads/leads/column spans to qualify as Kanban N+1 noise. */
const LEADS_COLUMN_N1_THRESHOLD = 3;

const LEADS_ROUTE_RE = /\/leads(?:[/?#]|$)/;
const LEADS_COLUMN_SPAN_RE = /\/api\/reads\/leads\/column/;

export function isKanbanColumnN1PerfNoise(event: TransactionEvent): boolean {
  // Raw SDK TransactionEvents carry NO `level` — @sentry/core builds the
  // transaction payload without one; Sentry's Relay stamps `level:info`
  // server-side AFTER this hook runs. The original strict `level === 'info'`
  // guard was written against the stored Sentry-UI issue shape, rejected every
  // real envelope, and left this filter dead in prod 2026-06-17 → 2026-07-22
  // (issue 127381445 regressed). Accept the raw shape (undefined) and the
  // stored shape ('info'); an explicit error/warning level passes through.
  const level = (event as { level?: string }).level;
  if (level !== undefined && level !== 'info') return false;
  if (event.exception?.values?.length) return false;

  // Must be the /leads route
  const transaction = event.transaction ?? '';
  if (!LEADS_ROUTE_RE.test(transaction)) return false;

  // Count spans matching the Kanban column fetch URL prefix
  const spans = event.spans ?? [];
  let columnSpanCount = 0;
  for (const span of spans) {
    const desc = span.description ?? '';
    if (LEADS_COLUMN_SPAN_RE.test(desc)) {
      columnSpanCount++;
      if (columnSpanCount >= LEADS_COLUMN_N1_THRESHOLD) return true;
    }
  }

  return false;
}
